import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  LinearProgress,
  Skeleton,
} from '@mui/material';
import {
  DirectionsCar,
  People,
  Gavel,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Assessment,
  Star,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { liveOperationsService, LiveMetrics } from '@/services/liveOperationsService';

interface MetricsCardsProps {
  timeRange: string;
}

interface MetricData {
  title: string;
  value: string | number;
  change: number;
  changeType: 'increase' | 'decrease';
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  progress?: number;
}

export const MetricsCards: React.FC<MetricsCardsProps> = ({ timeRange }) => {
  const theme = useTheme();
  const [metrics, setMetrics] = useState<LiveMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch real metrics data
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const liveMetrics = await liveOperationsService.getLiveMetrics();
        setMetrics(liveMetrics);
      } catch (err) {
        console.error('❌ Error fetching live metrics:', err);
        setError('Failed to load metrics');
        
        // Use fallback data
        setMetrics({
          activeRentals: 0,
          pendingHires: 0,
          activeDisputes: 0,
          totalRevenue24h: 0,
          averageTip: 0,
          fleetUtilization: 0,
          customerSatisfaction: 0,
          avgResponseTime: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    
    return () => clearInterval(interval);
  }, [timeRange]);

  // Show loading state
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(8)].map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Skeleton variant="circular" width={48} height={48} />
                  <Skeleton variant="rectangular" width={60} height={24} />
                </Box>
                <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 0.5 }} />
                <Skeleton variant="text" sx={{ fontSize: '1.25rem', mb: 0.5 }} />
                <Skeleton variant="text" width="60%" />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  // Show error state
  if (error || !metrics) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error" align="center">
                {error || 'Failed to load metrics'}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Please try refreshing the page
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  // Transform real metrics into display format
  const metricsData: MetricData[] = [
    {
      title: 'Active Rentals',
      value: metrics.activeRentals,
      change: 12,
      changeType: 'increase',
      icon: <DirectionsCar />,
      color: theme.palette.primary.main,
      subtitle: 'Currently rented',
      progress: Math.min((metrics.activeRentals / 50) * 100, 100),
    },
    {
      title: 'Pending Hires',
      value: metrics.pendingHires,
      change: 3,
      changeType: 'decrease',
      icon: <People />,
      color: theme.palette.warning.main,
      subtitle: 'Awaiting verification',
      progress: Math.min((metrics.pendingHires / 25) * 100, 100),
    },
    {
      title: 'Disputes',
      value: metrics.activeDisputes,
      change: 0,
      changeType: 'decrease',
      icon: <Gavel />,
      color: theme.palette.error.main,
      subtitle: 'Active cases',
      progress: Math.min((metrics.activeDisputes / 20) * 100, 100),
    },
    {
      title: 'Revenue (24h)',
      value: `₦${(metrics.totalRevenue24h / 1000).toFixed(1)}k`,
      change: 15,
      changeType: 'increase',
      icon: <AttachMoney />,
      color: theme.palette.success.main,
      subtitle: 'Last 24 hours',
    },
    {
      title: 'Avg. Tip',
      value: `₦${metrics.averageTip}`,
      change: 8,
      changeType: 'increase',
      icon: <AttachMoney />,
      color: theme.palette.success.main,
      subtitle: 'Per completed trip',
    },
    {
      title: 'Fleet Utilization',
      value: `${metrics.fleetUtilization}%`,
      change: 5,
      changeType: 'increase',
      icon: <TrendingUp />,
      color: theme.palette.secondary.main,
      subtitle: 'Vehicles in use',
      progress: metrics.fleetUtilization,
    },
    {
      title: 'Customer Satisfaction',
      value: metrics.customerSatisfaction.toFixed(1),
      change: 2,
      changeType: 'increase',
      icon: <Star />,
      color: theme.palette.warning.main,
      subtitle: 'Average rating',
      progress: (metrics.customerSatisfaction / 5) * 100,
    },
    {
      title: 'Response Time',
      value: `${metrics.avgResponseTime}h`,
      change: 8,
      changeType: 'decrease',
      icon: <Assessment />,
      color: theme.palette.success.main,
      subtitle: 'Avg. SLA response',
    },
  ];

  const formatChange = (change: number, changeType: 'increase' | 'decrease') => {
    const color = changeType === 'increase' ? 'success' : 'error';
    const icon = changeType === 'increase' ? <TrendingUp /> : <TrendingDown />;
    
    return (
      <Chip
        icon={icon}
        label={`${change}%`}
        size="small"
        color={color}
        variant="outlined"
        sx={{
          height: 20,
          '& .MuiChip-icon': {
            fontSize: '0.875rem',
          },
          '& .MuiChip-label': {
            fontSize: '0.75rem',
            px: 0.5,
          },
        }}
      />
    );
  };

  return (
    <Grid container spacing={3}>
      {metricsData.map((metric, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Card
            sx={{
              height: '100%',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
              },
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <Avatar
                  sx={{
                    backgroundColor: metric.color,
                    width: 48,
                    height: 48,
                  }}
                >
                  {metric.icon}
                </Avatar>
                {formatChange(metric.change, metric.changeType)}
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 0.5,
                  color: 'text.primary',
                }}
              >
                {metric.value}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  color: 'text.primary',
                }}
              >
                {metric.title}
              </Typography>

              {metric.subtitle && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: metric.progress ? 1 : 0 }}
                >
                  {metric.subtitle}
                </Typography>
              )}

              {metric.progress !== undefined && (
                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={metric.progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'action.selected',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: metric.color,
                        borderRadius: 3,
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {metric.progress}% of capacity
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};